import { PatientCaseContent, Variation } from '@llx/common';
import { CaseMap } from '@llx/common';
import gql from 'graphql-tag';
import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  CaseClusterFieldsFragment,
  ContextType,
  HostFieldsFieldsFragment,
  MeetingFieldsFragment,
  MeetingRelationCaseClusterFragment,
  useCurrentMeetingDataContextProviderQueryQuery,
  useGetHostFieldsQuery,
} from '@/@generated/codegen/API';
import {
  LoggedType,
  useUserAuthContext,
} from '@/components/AuthTokenContextProvider/UserAuthContext';
import useSlideVariations from '@/components/CaseSlide/hooks/useSlideVariation';
import { getPatientCaseIndexInCluster } from '@/features/caseCluster/helpers/caseCluster.helpers';
import usePatientCaseContent from '@/features/caseCluster/hooks/usePatientCaseContent';
import useMeetingStatsData from '@/features/meeting-stats/hooks/useMeetingStatsData';
import { useCreateFakeMeetingUtils } from '@/hooks/useCreateFakeMeetingUtils';
import { useEntitySubscription } from '@/hooks/useEntitySubscription';
import { useLiveMeetingParticipants } from '@/hooks/useLiveMeetingParticipants';
import { usePinchAnimationStore } from '@/hooks/usePinchAnimationStore';
import { createPinchingAnimationMeetingUniqueKey } from '@/utils/createPinchingAssetCookieKey';

import useRedirectHomeIfValueIsTrue from '../answer/hooks/useRedirectHomeIfMeetingIsArchived';
import useMeetingInteractionFunctions from '../hooks/useMeetingInteractionFunctions';

type Props = {
  children: React.ReactNode;
};

type BaseContextType = {
  currentMeetingId: string | null;
  setCurrentMeetingId: Dispatch<SetStateAction<string | null>>;
  createAndJoinFakeMeeting: ReturnType<
    typeof useCreateFakeMeetingUtils
  >['createAndJoinFakeMeeting'];
};

type CurrentMeetingDataContextNotInMeeting = BaseContextType & {
  status: 'not-in-meeting';
};

type CurrentMeetingDataContextInMeeting = BaseContextType & {
  status: 'in-meeting';
  currentMeetingId: string;
  setCurrentMeetingId: Dispatch<SetStateAction<string | null>>;
  currentMeeting: MeetingFieldsFragment & MeetingRelationCaseClusterFragment;
  hostFields: HostFieldsFieldsFragment;
  hostID: string;
  caseCluster: CaseClusterFieldsFragment;
  caseClusterName: string;
  caseMap: CaseMap;
  currentCase: PatientCaseContent | null;
  currentPatientCaseId: string | null;
  currentCaseIndex: number;
  isCaseLoading: boolean;
  slideIndex: number;
  slideID: string | null;
  slideName: string | null;
  isUserHost: boolean;
  meetingStatsData: ReturnType<typeof useMeetingStatsData>;
  variationList: Variation[];
  connectedUserList: ReturnType<typeof useLiveMeetingParticipants>;
  meetingInteractions: ReturnType<typeof useMeetingInteractionFunctions>;
  isPreview: boolean;
  slideVariationDetails: ReturnType<typeof useSlideVariations>;
  currentHostAssetID: string | null | undefined;
};

export type CurrentMeetingDataContextType =
  | CurrentMeetingDataContextNotInMeeting
  | CurrentMeetingDataContextInMeeting;

export const CurrentMeetingDataContext = createContext<CurrentMeetingDataContextType | undefined>(
  undefined
);

gql`
  query CurrentMeetingDataContextProviderQuery($where: MeetingWhereUniqueInput!) {
    getMeeting(where: $where) {
      ...MeetingFields
      ...MeetingRelationCaseCluster
    }
  }
`;

export default function CurrentMeetingDataContextProvider({ children }: Props) {
  const [currentMeetingId, setCurrentMeetingId] = useState<string | null>(null);
  const authContext = useUserAuthContext();

  const { createAndJoinFakeMeeting, isInFakeMeeting: isPreview } = useCreateFakeMeetingUtils({
    currentMeetingId,
    setCurrentMeetingId,
  });
  const currentMeetingQuery = useCurrentMeetingDataContextProviderQueryQuery({
    variables: {
      where: {
        id: currentMeetingId,
      },
    },
    skip: !currentMeetingId,
    fetchPolicy: isPreview ? 'cache-only' : 'cache-and-network',
  });
  const currentMeeting = useMemo(() => {
    if (!currentMeetingId) return null;
    return currentMeetingQuery.data?.getMeeting ?? null;
  }, [currentMeetingQuery.data, currentMeetingId]);

  const hostFieldsQuery = useGetHostFieldsQuery({
    variables: {
      where: {
        meetingId: currentMeetingId,
      },
    },
    skip: !currentMeetingId,
    fetchPolicy: isPreview ? 'cache-only' : 'cache-and-network',
  });
  const hostFields = useMemo(() => {
    if (!currentMeetingId) return null;
    return hostFieldsQuery.data?.getHostFields ?? null;
  }, [hostFieldsQuery.data, currentMeetingId]);

  const caseContent = usePatientCaseContent(
    currentMeeting?.caseCluster?.cases.edges.map((el) => el.node.id) || []
  );

  const userID = useMemo(() => {
    if (authContext.status !== LoggedType.LOGGED_IN) return null;
    return authContext.userID;
  }, [authContext.status]);

  useEntitySubscription('Meeting', {
    onUpdate: {
      variables: {
        where: {
          id: {
            equals: currentMeetingId,
          },
        },
      },
      skip: !currentMeetingId,
    },
  });

  useEntitySubscription('HostFields', {
    onUpdate: {
      variables: {
        where: {
          meetingId: {
            equals: currentMeetingId,
          },
        },
      },
      skip: !currentMeetingId,
    },
  });

  const currentCaseIndex = useMemo(() => {
    if (!hostFields) return -1;
    return getPatientCaseIndexInCluster(
      hostFields.currentCase,
      currentMeeting?.caseCluster ?? null
    );
  }, [hostFields, currentMeeting?.caseCluster]);

  const currentCaseContent = useMemo(() => {
    if (caseContent.isCaseMapLoading || !hostFields || !hostFields.currentCase) return null;
    return caseContent.caseMap[hostFields.currentCase] ?? null;
  }, [caseContent, hostFields]);

  const variationList: Variation[] = useMemo(() => {
    return currentCaseContent?.variations || [];
  }, [currentCaseContent]);

  const isUserHost = useMemo(() => {
    return !!userID && currentMeeting?.hostId === userID;
  }, [userID, currentMeeting]);

  const meetingStatsData = useMeetingStatsData(!isPreview ? currentMeeting?.id : null, true);
  const currentSlide = useMemo(() => {
    if (!hostFields || !currentCaseContent) return null;
    return currentCaseContent.slides[hostFields.currentSlide || 0];
  }, [currentCaseContent, hostFields]);
  const connectedUserList = useLiveMeetingParticipants(!isPreview ? currentMeeting?.id : null);

  const meetingInteractions = useMeetingInteractionFunctions(
    currentMeeting,
    hostFields,
    isPreview ?? false
  );

  const slideVariationDetails = useSlideVariations(
    ContextType.Meeting,
    currentMeeting?.id ?? null,
    hostFields?.currentCase,
    isUserHost,
    currentSlide,
    variationList,
    userID
  );

  const currentHostAssetID = hostFields?.currentAssetID;

  const [initPinchAnimationState] = usePinchAnimationStore((state) => [
    state.initPinchAnimationState,
  ]);

  useEffect(() => {
    if (!currentMeeting?.id) return;

    const pinchAnimationUniqueKey = createPinchingAnimationMeetingUniqueKey(
      currentMeeting.id,
      currentMeeting.startDate?.toString() ?? null,
      currentMeeting.isInTesting
    );
    initPinchAnimationState(pinchAnimationUniqueKey);
  }, [
    initPinchAnimationState,
    currentMeeting?.id,
    currentMeeting?.startDate,
    currentMeeting?.isInTesting,
  ]);

  useRedirectHomeIfValueIsTrue({
    value: isPreview ? undefined : currentMeeting?.isArchived,
  });

  const contextValue: CurrentMeetingDataContextType =
    currentMeetingId && currentMeeting && hostFields
      ? {
          status: 'in-meeting',
          currentMeetingId,
          setCurrentMeetingId,
          currentMeeting: currentMeeting,
          hostFields,
          hostID: currentMeeting.hostId,
          caseCluster: currentMeeting.caseCluster,
          caseClusterName: currentMeeting.caseCluster?.name ?? '',
          caseMap: caseContent.caseMap,
          currentCase: currentCaseContent,
          currentPatientCaseId: hostFields.currentCase ?? null,
          currentCaseIndex,
          isCaseLoading: caseContent.isCaseMapLoading,
          slideIndex: hostFields.currentSlide ?? 0,
          slideName: currentSlide ? currentSlide.title : null,
          slideID: currentSlide ? currentSlide.id : null,
          isUserHost,
          meetingStatsData,
          variationList,
          connectedUserList,
          meetingInteractions,
          isPreview: isPreview ?? false,
          slideVariationDetails,
          currentHostAssetID,
          createAndJoinFakeMeeting,
        }
      : {
          status: 'not-in-meeting',
          currentMeetingId,
          setCurrentMeetingId,
          createAndJoinFakeMeeting,
        };

  return (
    <CurrentMeetingDataContext.Provider value={contextValue}>
      {children}
    </CurrentMeetingDataContext.Provider>
  );
}

export const useCurrentMeetingDataContext = (): CurrentMeetingDataContextInMeeting => {
  const context = useContext(CurrentMeetingDataContext);
  if (context === undefined) {
    throw new Error(
      'useCurrentMeetingDataContext must be used within a CurrentMeetingDataContextProvider'
    );
  }
  if (context.status === 'not-in-meeting') {
    throw new Error(
      'useCurrentMeetingDataContext must be used when you are in a meeting otherwise use useNotInMeetingContext'
    );
  }
  return context;
};

export const useNotInMeetingContext = () => {
  const context = useContext(CurrentMeetingDataContext);
  if (context === undefined) {
    throw new Error(
      'useNotInMeetingContext must be used within a CurrentMeetingDataContextProvider'
    );
  }
  return context;
};
